<script lang="ts">
	//Stores
	import { devices } from 'src/stores/lists/devices';
	// Services
	import { format } from 'svelte-i18n';
	//Design System Components
	import {
		CircleAvatar,
		LabelList,
		LabelListV2,
		Modal,
		Container,
		Text,
		Button,
		Countdown,
	} from '@Shelly/shelly-design-system';
	import type { AvatarColorVariantTypes } from '@Shelly/shelly-design-system';
	//Helpers
	import { load_image_from, shellyId16 } from 'src/helpers/interface_helpers';
	import { extractDevicesFromAlarm, formatSecsToTimeString } from '../../list-helpers';
	import {
		classMap,
		convertSecondsToDDHHMMSS,
	} from '@Shelly/shelly-design-system/helpers/component-utils';
	// Types
	import type { IAlarmZone } from 'src/components/AlarmZones/AlarmWizard/alarm_object';
	import { LabelTypes } from '@Shelly/shelly-design-system/Label/label-helpers';
	import AlarmDelayTimeCountdown from './AlarmDelayTimeCountdown.svelte';
	import { checkIfColorIsAvatarVariant } from 'src/helpers/utilities';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { mapIconSolidWeightToLight, mapLegacyColorToToken } from 'src/helpers/new-layout-helpers';

	export let alarm: IAlarmZone;
	export let showModal = false;
	export let hideLabel = false;
	export let timer: number | undefined;
	export let hasMissing: boolean = false;

	const labelListWithMissingDevices = [
		{
			icon: 'fas fa-warning',
			id: 0,
			value: $format('missing_device_label', { default: 'Missing Device' }),
			type: LabelTypes.WARNING,
		},
	];

	const checkForMissingDevice = (deviceIds: string[]): boolean => {
		return deviceIds.some((id) => {
			const decimal = id.split(':')[1];
			const device = decimal ? $devices[shellyId16(decimal)] : undefined;
			return !device;
		});
	};

	const allDevices = extractDevicesFromAlarm(alarm);
	hasMissing = checkForMissingDevice(allDevices);

	const getAlarmBackgroundColor = (color: string | undefined): AvatarColorVariantTypes | null => {
		if (!color || !checkIfColorIsAvatarVariant(color)) {
			return null;
		}

		return color as AvatarColorVariantTypes;
	};

	const onModalClose = () => {
		showModal = false;
	};
</script>

<AlarmDelayTimeCountdown {alarm} bind:time={timer} />

{#if timer && !hideLabel}
	{#if $showNewLayout}
		<LabelListV2
			labelList={[
				...(hasMissing
					? labelListWithMissingDevices.map((el) => ({
							...el,
							icon: mapIconSolidWeightToLight(el?.icon),
							backgroundColor: mapLegacyColorToToken(el?.type),
						}))
					: []),
				{
					value: formatSecsToTimeString(timer),
					icon: 'fas fa-clock',
					backgroundColor: '--color-primary',
				},
			]}
			type="default"
			size="m"
			a11yProps={{ isInteractive: true }}
		/>
	{:else}
		<LabelList
			labelList={[
				...(hasMissing ? labelListWithMissingDevices : []),
				{
					value: formatSecsToTimeString(timer),
					icon: 'fas fa-clock',
					type: 'primary',
				},
			]}
			type="default"
			size="m"
		/>
	{/if}
{/if}

{#if hasMissing && !timer && !hideLabel}
	{#if $showNewLayout}
		<LabelListV2
			labelList={labelListWithMissingDevices.map((el) => ({
				...el,
				icon: mapIconSolidWeightToLight(el?.icon),
				backgroundColor: mapLegacyColorToToken(el?.type),
			})) || []}
			type="default"
			size="m"
			a11yProps={{ isInteractive: true }}
		/>
	{:else}
		<LabelList labelList={labelListWithMissingDevices} type="default" size="m" />
	{/if}
{/if}

{#if showModal && alarm._enabled}
	<Modal
		on:close={onModalClose}
		{showModal}
		title={!timer
			? $format('title_alarm_armed', {
					default: 'Alarm Armed',
				})
			: $format('label_alarm_progress', {
					default: 'Arming in Progress',
				})}
	>
		<Container slot="content" padding="0">
			<div class="d-flex flex-direction-column align-items-center">
				<CircleAvatar
					class="mb-l"
					size="xxl"
					borderStatus={{
						icon: !timer ? 'fas fa-check' : 'fas fa-clock',
						color: !timer ? 'color-success' : 'color-primary',
					}}
					image={alarm._meta.image && load_image_from(alarm._meta.image)}
					backgroundColor={getAlarmBackgroundColor(alarm._meta.backgroundColor)}
				/>
				<Text
					class={classMap({
						'mb-m': timer,
						'mb-s': !timer,
						'text-center': true,
					})}
					styleType="h1"
				>
					{alarm._meta.name}
					{!timer
						? $format('label_armed_success', { default: 'Armed Successfully' })
						: $format('label_will_arm_after_delay', {
								default: 'will be armed after delay time expires.',
							})}
				</Text>

				<Text class="text-color-muted mb-s">
					{!timer
						? $format('label_armed_active_monitoring', {
								default: 'Your alarm is now active and monitoring',
							})
						: $format('label_arm_in', { default: 'Arming alarm in' })}</Text
				>

				{#if timer}
					<Countdown
						time={convertSecondsToDDHHMMSS(timer)}
						timeTexts={{
							days: $format('label_d_countdow', { default: 'd' }),
							hours: $format('label_h_countdow', { default: 'h' }),
							minutes: $format('label_m_countdow', { default: 'm' }),
							seconds: $format('label_s_countdow', { default: 's' }),
						}}
					/>
				{/if}

				<Button class="mt-m" on:click={onModalClose}>
					{$format('button_got_it_no_sign', { default: 'Okay, got it' })}
				</Button>
			</div>
		</Container>
	</Modal>
{/if}
